/**
 * Multimodal context memory — single read-only snapshot of everything the
 * agents in this app might need to reason about across chat / studio / world
 * / code surfaces.
 *
 * This is intentionally a one-way aggregator. Each surface keeps its own
 * authoritative store; `getAppContext()` just reads them and packages a
 * compact JSON snapshot. Agents call it (or read window.protoContext) before
 * generating, so a "make the title glitch in" or "insert that on track 2"
 * resolves to the right place without manual plumbing.
 */
import { useSession } from '../store/session';
import { useSceneStore } from '../world/useSceneStore';
import { useCodeProject } from '../code/useCodeProject';
import type { Mode } from '../router/types';

export interface AppContext {
  /** Active chat session messages (last 20). */
  chat: {
    sessionId: string | null;
    mode: Mode;
    directorAgent: string;
    recentMessages: Array<{
      role: string;
      content: string;
      createdAt: number;
      attachmentKinds?: string[];
    }>;
  };
  /** Current timeline state (if Studio has been visited). */
  studio?: {
    trackCount: number;
    clipCount: number;
    clipTypes: Record<string, number>;
    durationSec: number;
    currentTime: number;
  };
  /** The chat-side multi-asset 3D world. */
  world: {
    assetCount: number;
    assetTags: string[];
    environment: string | null;
    lightCount: number;
  };
  /** Code editor project (if any). */
  code: {
    fileCount: number;
    activePath: string | null;
    fileSummary: Array<{ name: string; size: number }>;
    taskCount: number;
  };
}

/** Snapshot the whole app for an agent prompt. Cheap — no IO. */
export function getAppContext(): AppContext {
  const session = useSession.getState();
  const world = useSceneStore.getState();
  const code = useCodeProject.getState();

  const recent = session.messages.slice(-20).map((m) => ({
    role: m.role,
    content: typeof m.content === 'string' ? m.content : JSON.stringify(m.content),
    createdAt: m.createdAt,
    attachmentKinds: m.attachments?.map((a) => a.kind),
  }));

  let studio: AppContext['studio'] | undefined;
  // The studio store only exists once /studio has been mounted.
  try {
     
    const ed = (window as any).__protocutEditor ?? null;
    if (ed) {
      const s = ed.getState?.();
      if (s) {
        const types: Record<string, number> = {};
        for (const c of s.clips) types[c.type] = (types[c.type] ?? 0) + 1;
        studio = {
          trackCount: s.tracks.length,
          clipCount: s.clips.length,
          clipTypes: types,
          durationSec: s.duration,
          currentTime: s.currentTime,
        };
      }
    }
  } catch { /* studio not loaded */ }

  return {
    chat: {
      sessionId: session.activeId,
      mode: session.mode,
      directorAgent: session.directorAgent,
      recentMessages: recent,
    },
    studio,
    world: {
      assetCount: world.assets.length,
      assetTags: world.assets.map((a) => a.tag).filter(Boolean) as string[],
      environment: world.environment.preset,
      lightCount: world.lights.length,
    },
    code: {
      fileCount: code.files.length,
      activePath: code.activePath,
      fileSummary: code.files.map((f) => ({ name: f.name, size: f.content.length })),
      taskCount: code.tasks.length,
    },
  };
}

/** Markdown-friendly context block to inject into director system prompts. */
export function getAppContextMarkdown(): string {
  const ctx = getAppContext();
  const lines: string[] = ['## App context (auto-supplied — don\'t echo back)'];
  lines.push(`- mode: **${ctx.chat.mode}** · director: ${ctx.chat.directorAgent} · session: ${ctx.chat.sessionId ?? '-'}`);
  if (ctx.studio) {
    lines.push(`- studio: ${ctx.studio.clipCount} clips on ${ctx.studio.trackCount} tracks, t=${ctx.studio.currentTime.toFixed(2)}s`);
    const types = Object.entries(ctx.studio.clipTypes).map(([k, v]) => `${k}=${v}`).join(', ');
    if (types) lines.push(`  types: ${types}`);
  }
  if (ctx.world.assetCount > 0) {
    lines.push(`- 3d world: ${ctx.world.assetCount} assets [${ctx.world.assetTags.join(', ')}], env=${ctx.world.environment ?? 'none'}, lights=${ctx.world.lightCount}`);
  }
  if (ctx.code.fileCount > 0) {
    lines.push(`- code: ${ctx.code.fileCount} files (active=${ctx.code.activePath ?? '-'}, tasks=${ctx.code.taskCount})`);
  }
  return lines.join('\n');
}

let installed = false;
export function installContextGlobal() {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  (window as any).protoContext = { get: getAppContext, getMarkdown: getAppContextMarkdown };
}
